import cn from 'classnames'
import {
  Beta,
  IconArrowLeftDefault,
  IconArrowRightDefault,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import {
  TrackingAttributes,
  TrackClick,
  TrackVisible,
} from 'pages/certificates/tracking'
import { LinkedInTestimonialCard } from './LinkedInTestimonialCard'
import { type Testimonial } from './types'
import styles from './LinkedInTestimonialsContent.module.scss'

const MAX_LINKEDIN_TESTIMONIALS = 6

interface LinkedInTestimonialsContentProps {
  testimonials: Testimonial[]
  title?: string
}

export const LinkedInTestimonialsContent = ({
  testimonials,
  title,
}: LinkedInTestimonialsContentProps) => {
  const { t } = useTranslation('@mc/certificates')

  const limitedTestimonialCount = testimonials.length <= 1
  const displayed = testimonials.slice(0, MAX_LINKEDIN_TESTIMONIALS)
  return (
    <section className='my-11 container'>
      <h2 className='c-heading-1 text-c-text-dark mb-8 text-center'>
        {title ??
          t('marketingPages.certificate.linkedInTestimonials.sectionTitle')}
      </h2>

      <Beta.CarouselV2.Carousel
        srLabel={t(
          'marketingPages.certificate.linkedInTestimonials.srCarouselLabel',
        )}
        scrollCount={1}
      >
        <Beta.CarouselV2.CarouselContent
          overflow
          scrollSnapAlign='center'
          focusOpacity={0.6}
          className={cn(
            'items-start md:items-stretch gap-4 md:gap-7',
            styles.carouselContent,
            {
              'md:justify-center': limitedTestimonialCount,
            },
          )}
        >
          {displayed.map((testimonial, i) => (
            <TrackingAttributes
              key={`${testimonial.customer?.name}-${i}`}
              tile_position_index={i}
              tile_title={testimonial.customer?.name}
              tile_subtitle={testimonial.customer?.title}
              tile_link={testimonial.sourceUrl}
            >
              <TrackVisible
                event='certificateRowTileViewed'
                className='col-11 col-md-8 col-xl-7 px-0 py-2 min-w-0 flex flex-col'
              >
                <Beta.CarouselV2.CarouselItem>
                  <LinkedInTestimonialCard testimonial={testimonial} />
                </Beta.CarouselV2.CarouselItem>
              </TrackVisible>
            </TrackingAttributes>
          ))}
        </Beta.CarouselV2.CarouselContent>

        {!limitedTestimonialCount && (
          <div className='hidden md:flex justify-center gap-2 mt-6 items-center mc-lp--carousel--controls'>
            <TrackClick event='certificateRowScrolled' direction='left'>
              <Beta.CarouselV2.CarouselPrevious
                kind='link'
                size='md'
                symmetrical
                alwaysShowControls={!limitedTestimonialCount}
                className='p-4'
              >
                <IconArrowLeftDefault size='md' />
              </Beta.CarouselV2.CarouselPrevious>
            </TrackClick>

            <TrackClick event='certificateRowScrolled' direction='right'>
              <Beta.CarouselV2.CarouselNext
                kind='link'
                size='md'
                symmetrical
                alwaysShowControls={!limitedTestimonialCount}
                className='p-4'
              >
                <IconArrowRightDefault size='md' />
              </Beta.CarouselV2.CarouselNext>
            </TrackClick>
          </div>
        )}
      </Beta.CarouselV2.Carousel>
    </section>
  )
}
